import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Col, Form, Input, notification, Popover, Row, Select, Space, Typography, Upload } from 'antd'
import { getCountryCallingCode, isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input'
import { CaretDownOutlined, CloseCircleOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons'
import { RcFile } from 'antd/lib/upload/interface'
import ImgCrop from 'antd-img-crop'

import momentTimezone from 'moment-timezone'
import ProfileAvatar from '../../../helpers/ProfileAvatar/ProfileAvatar'

import { resizeImage, uploadFile } from '../../../../context/avatar'
import { useAuthContext } from '../../../../context/auth'
import { IAccount, ILanguage, ISocialLink, TAccountRequest, TState } from '../../../../context/types'
import { getStatesByCountry } from '../../../../context/states'
import { useCountriesContext } from '../../../../context/countries'
import { usePageLeaveContext } from '../../../../context/pageLeave'
import Countries from '../../../../utils/countries'
import ChangeExpertStatusModal from '../../admin/users/ChangeExpertStatusModal'
import { useExpertProfilesContext } from '../../../../context/expertProfile'
import { statuses } from '../../../helpers/ExpertStatusSelect/ExpertStatusSelect'
import ISO6391 from 'iso-639-1'
import { FormListFieldData } from 'antd/lib/form/FormList'
import * as networks from 'react-social-icons/build/networks'
import { SocialIcon } from 'react-social-icons'
import { SocialLinks } from 'social-links'
import { languagesOptions } from '../../../../utils/languages'
import { useAccountContext } from '../../../../context/account'
import { useParams } from 'react-router-dom'
import { REGEX_CITY, DEFAULT_COUNTRY } from '../../../../constants'
import { useChannelContext } from '../../../../context/channel'
import ResetPasswordModal from './ResetPasswordModal'
import classNames from 'classnames'
import 'antd/es/slider/style'

const { Paragraph, Title } = Typography
const { Option } = Select

const regex = new RegExp(/[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)?/gi)

const socialLinks = new SocialLinks()

export type TAccountFormProps = {
	userData: IAccount
}

type UrlParam = { accountId: string }

function AccountForm({ userData }: TAccountFormProps) {
	const [form] = Form.useForm()
	const { auth, actions } = useAuthContext()
	const { actions: accountActions } = useAccountContext()
	const { pageLeave, actions: pageLeaveActions } = usePageLeaveContext()
	const { expertProfiles } = useExpertProfilesContext()

	const { accountId } = useParams() as UrlParam
	const [states, setStates] = useState<TState[]>([])
	const [links, setLinks] = useState<string[]>([])
	const [resetPasswordModalVisibility, setResetPasswordModalVisibility] = useState<boolean>(false)
	const [isExpertStatusModalVisible, setExpertStatusModalVisible] = useState(false)
	const [loadingPictureUpload, setLoadingPictureUpload] = useState(false)
	const [formErrors, setFormErrors] = useState([])
	const [stateSearch, setStateSearch] = useState<string>('')
	const [countrySearch, setCountrySearch] = useState<string>('')
	const { channel } = useChannelContext()
	const {
		countries: { data: countries, loading: countryLoading },
		actions: countryActions,
	} = useCountriesContext()

	useEffect(() => {
		!accountId ? actions.setError() : accountActions.setError()
		countryActions.getList()
	}, [])

	const isPhoneRequired = useMemo(
		() =>
			accountId
				? false
				: userData?.user?.isExpert
				? (channel?.channels || []).reduce(
						(acc, { integration_config, is_expert }) =>
							acc || (is_expert && (integration_config?.phone_rules?.phone_required_to_register_expert ?? true)),
						false
				  )
				: (channel?.channels || []).reduce(
						(acc, { integration_config, is_expert }) =>
							acc || (!is_expert && integration_config?.phone_rules?.phone_required_to_register_member),
						false
				  ),
		[channel?.channels?.length, userData?.user?.isExpert]
	)

	const initialValues = useMemo<{ [key: string]: any }>(
		() => ({
			email: userData?.user?.email,
			first_name: userData?.user?.first_name,
			last_name: userData?.user?.last_name,
			notification_email: userData?.user?.notification_email || '',
			eeo_is_disabled: userData?.user?.eeo_is_disabled || false,
			eeo_is_lgbtq: userData?.user?.eeo_is_lgbtq || false,
			eeo_is_veteran: userData?.user?.eeo_is_veteran || false,
			eeo_ethnicity_id: userData?.user?.ethnicity?.id,
			eeo_gender_id: userData?.user?.gender?.id,
			countryId: userData?.user?.country?.id,
			stateId: userData?.user?.state?.id,
			city: userData?.user?.city,
			zip: userData?.user?.zip,
			timezone: userData?.user?.timezone,
			socialLinks: userData?.user?.socialLinks || [],
			languages:
				userData?.user?.languages?.length > 0 ? userData?.user?.languages?.map((l: ILanguage) => l.iso) || [] : ['en'],
			phone_country_code:
				userData?.user?.phone_country_code ||
				parsePhoneNumber(`+${userData?.user?.phone_country_id}${userData?.user?.phone}`)?.country ||
				DEFAULT_COUNTRY.iso,
			phone_country_id: Countries.options.some(
				({ country }) => getCountryCallingCode(country) === userData?.user?.phone_country_id?.toString()
			)
				? userData?.user?.phone_country_id?.toString()
				: '1',
			phone: userData?.user?.phone,
		}),
		[userData?.user, userData?.user?.socialLinks, userData?.user?.languages]
	)

	useEffect(() => {
		form.setFieldsValue(initialValues)
		setLinks(initialValues.socialLinks.map((link: ISocialLink) => link.url))
	}, [form, initialValues])

	useEffect(() => {
		if (userData?.user?.country?.id) {
			getStatesByCountry(userData?.user?.country?.id)
				.then((stateData: TState[]) => setStates(stateData))
				.catch()
		}
	}, [userData?.user?.country?.id])

	const isFormDirty = useMemo(() => {
		const values = form.getFieldsValue()
		return Object.keys(values).reduce((isDirty: boolean, key: string) => {
			return isDirty || JSON.stringify(values[key]) !== JSON.stringify(initialValues[key])
		}, false)
	}, [userData?.user, userData?.user?.socialLinks, userData?.user?.languages, form.getFieldsValue()])

	useEffect(() => {
		pageLeaveActions.setConditions({
			...pageLeave.conditions,
			account: isFormDirty,
		})
	}, [isFormDirty])

	const reset = useCallback(() => (!accountId ? actions.setError() : accountActions.setError()), [
		actions,
		accountActions,
	])

	const onFinish = (values: TAccountRequest) => {
		!accountId
			? actions.updateAccount({ ...values, profile_pic_url: userData?.user.profile_pic_url })
			: accountActions.updateAccount({ ...values, profile_pic_url: userData?.user.profile_pic_url })
	}

	const handleCountryChange = (value: string) => {
		form.setFieldsValue({ stateId: null })
		getStatesByCountry(value)
			.then((stateData: TState[]) => setStates(stateData))
			.catch()
	}

	const onFieldsChange = () => {
		const nextFormErrors = form.getFieldsError().filter((v) => v.errors.length)
		setFormErrors(nextFormErrors)
	}

	const handleChangeAvatar = (file: RcFile) => {
		const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png'

		if (!isJpgOrPng) {
			notification.error({ message: 'You can only upload JPG/PNG file!' })
			return
		}

		if (loadingPictureUpload) return

		setLoadingPictureUpload(true)

		resizeImage(file)
			.then((r) => uploadFile(r))
			.then((r) => {
				!accountId
					? actions.updateAccount({ ...form.getFieldsValue(), profile_pic_url: r.image.url })
					: accountActions.updateAccount({ ...form.getFieldsValue(), profile_pic_url: r.image.url })
				notification.success({ message: 'File uploaded' })
			})
			.catch(() => notification.error({ message: 'File upload failed' }))
			.finally(() => setLoadingPictureUpload(false))

		return false
	}

	const triggerValidatePhoneField = useCallback(
		(option) => {
			form.setFieldsValue({
				phone_country_id: getCountryCallingCode(option),
			})
			form.validateFields(['phone'])
		},
		[form]
	)

	const prefixSelector = (
		<>
			<Form.Item name="phone_country_code" noStyle>
				<Select
					filterOption={(input, option) => {
						return (
							option['data-code'].toLowerCase().indexOf(input.toLowerCase().replace('+', '')) >= 0 ||
							option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
						)
					}}
					showSearch
					style={{ width: 105 }}
					onChange={triggerValidatePhoneField}
				>
					{Countries.options.map(({ code, flag, country }, index) => (
						<Option key={index} value={`${country}`} data-code={code}>
							<span className="country-flag">{flag}</span>
							<span className="country-code">+{code}</span>
						</Option>
					))}
				</Select>
			</Form.Item>
			<Form.Item name="phone_country_id" style={{ display: 'none' }}>
				<Input disabled={!!userData?.loading} />
			</Form.Item>
		</>
	)

	const onChangeUrl = (idx: number) => (e: ChangeEvent<HTMLInputElement>) => {
		const newLinks = [...links]
		newLinks[idx] = e.target.value
		setLinks(newLinks)
	}

	const onLanguagesSearch = (searchText: string, option: any) => new RegExp(searchText, 'gi').test(option.children)

	const notificationEmailAlert = () => (
		<Alert
			type="info"
			showIcon
			message=""
			style={{ marginBottom: 10 }}
			description="If you registered through LinkedIn or Google, they store your email address in the “email address” field. If you would like to send call scheduling alerts to another email address (eg. personal email or scheduling assistant) please input that here."
		/>
	)

	const filterSort = (search: string) => (optionA: any, optionB: any): number => {
		const indexA = optionA.children.toLowerCase().indexOf(search.toLowerCase())
		const indexB = optionB.children.toLowerCase().indexOf(search.toLowerCase())

		return indexA < indexB ? -1 : indexA > indexB ? 1 : 0
	}
	const currentProfile = useMemo(
		() => expertProfiles.profiles.find((p) => p.brand.root_url === window.location.origin),
		[expertProfiles]
	)
	const getUpdateStatusText = () => {
		const status = statuses[currentProfile?.status]
		return (
			<>
				{status} <CaretDownOutlined />
			</>
		)
	}
	const isPasswordProvider = !auth.user?.providerId || auth.user?.providerId === 'password'

	return (
		<>
			<Form
				name="AccountForm"
				form={form}
				onFinish={onFinish}
				scrollToFirstError
				layout="vertical"
				onValuesChange={reset}
				onFieldsChange={onFieldsChange}
				initialValues={initialValues}
			>
				<Row gutter={[50, 0]} align="top">
					<Col xs={24} lg={10}>
						<Space direction={'horizontal'} size={20} style={{ marginBottom: 48 }}>
							<ImgCrop rotate shape="rect">
								<Upload
									name="avatar"
									listType="picture"
									className={classNames({
										'avatar-uploader': true,
										disabled: loadingPictureUpload,
									})}
									showUploadList={false}
									beforeUpload={handleChangeAvatar}
									disabled={loadingPictureUpload}
								>
									{loadingPictureUpload && (
										<div className="profile-loader__wrapper">
											<LoadingOutlined size={140} className="profile-loader" />
										</div>
									)}
									<ProfileAvatar
										className={classNames({
											'update-avatar': true,
											disabled: loadingPictureUpload,
										})}
										size={138}
										src={userData?.user?.profile_pic_url}
									/>
								</Upload>
							</ImgCrop>
							<div className="action-btns">
								{userData?.user?.isExpert && !!currentProfile && (
									<>
										<Button
											shape="round"
											className="blue-outlined-button"
											style={{ marginTop: 8, display: 'flex', alignItems: 'center' }}
											onClick={() => setExpertStatusModalVisible(true)}
										>
											{getUpdateStatusText()}
										</Button>
										<ChangeExpertStatusModal
											isVisible={isExpertStatusModalVisible}
											onChange={setExpertStatusModalVisible}
											user={userData.user}
										/>
									</>
								)}
							</div>
						</Space>
					</Col>
					<Col xs={24} lg={14}>
						<Row gutter={[25, 0]}>
							<Col xs={24} lg={12}>
								<Form.Item
									label="First Name"
									name="first_name"
									rules={[{ required: true, message: 'Please input your First Name!' }]}
								>
									<Input disabled={!!userData?.loading} />
								</Form.Item>
							</Col>
							<Col xs={24} lg={12}>
								<Form.Item
									label="Last Name"
									name="last_name"
									rules={[{ required: true, message: 'Please input your Last Name!' }]}
								>
									<Input disabled={!!userData?.loading} />
								</Form.Item>
							</Col>
							<Col xs={24} lg={12}>
								<Form.Item
									label="Email"
									name="email"
									rules={[{ type: 'email', required: true, message: 'Please input your Email address' }]}
								>
									<Input disabled={!!userData?.loading} />
								</Form.Item>
							</Col>
							<Col xs={24} lg={12}>
								<Form.Item
									label={
										<span>
											Notification email
											<Popover overlayClassName="what-popover" content={notificationEmailAlert} trigger={'hover'}>
												<strong className="what-link">{`What's this?`}</strong>
											</Popover>
										</span>
									}
									name="notification_email"
									rules={[{ type: 'email', message: 'Please input correct notification email' }]}
								>
									<Input disabled={!!userData?.loading} />
								</Form.Item>
							</Col>
						</Row>
						<Row>
							<Col xs={24}>
								<Form.Item label="Password">
									{isPasswordProvider && (
										<Button type="default" shape="round" onClick={() => setResetPasswordModalVisibility(true)}>
											Change Password
										</Button>
									)}
									{!isPasswordProvider && (
										<Alert
											style={{ backgroundColor: '#f4f7f8', borderColor: '#cdf1f6' }}
											showIcon
											type="info"
											message="There is no password associated with this email address as you signed up using another service (Google, LinkedIn)"
										/>
									)}
								</Form.Item>
							</Col>
						</Row>
					</Col>
					<Col xs={24} lg={10}>
						<Form.Item
							name="phone"
							label="Mobile Number"
							rules={[
								{
									validator: (_rule, value) => {
										const phoneCountryId = getCountryCallingCode(form.getFieldValue('phone_country_code'))
										if (!value || isValidPhoneNumber(`+${phoneCountryId}${value}`)) {
											return Promise.resolve()
										}
										return Promise.reject('The input is not valid mobile number!')
									},
									message: 'The input is not valid mobile number!',
								},
								{ required: isPhoneRequired, message: 'Please input your Mobile number!' },
							]}
						>
							<Input
								addonBefore={prefixSelector}
								style={{ width: '100%' }}
								type="number"
								disabled={!!userData?.loading}
							/>
						</Form.Item>
					</Col>
					<Col xs={24} lg={14}>
						{!countryLoading ? (
							<Form.Item name="countryId" label="Country">
								<Select
									showSearch
									onChange={handleCountryChange}
									optionFilterProp="name"
									onSearch={(input: string) => setCountrySearch(input)}
									filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
									filterSort={filterSort(countrySearch)}
								>
									{countries.map((country: any, index) => (
										<Option key={index} value={country.id}>
											{country.name}
										</Option>
									))}
								</Select>
							</Form.Item>
						) : null}
					</Col>
					<Col xs={24} lg={10}>
						<Form.Item
							label="City"
							name="city"
							rules={[
								{
									message: 'Please enter correct name',
									validator: (_, value) => {
										const regex = new RegExp(REGEX_CITY, 'ig')
										if (!regex.test(value)) {
											return Promise.reject()
										}
										return Promise.resolve()
									},
								},
							]}
						>
							<Input disabled={!!userData?.loading} />
						</Form.Item>
					</Col>
					<Col xs={24} lg={14}>
						{!countryLoading && states.length ? (
							<Form.Item name="stateId" label="State / Province">
								<Select
									showSearch
									optionFilterProp="name"
									filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
									onSearch={(input: string) => setStateSearch(input)}
									filterSort={filterSort(stateSearch)}
								>
									{states?.map((state: any, index) => (
										<Option key={index} value={state.id}>
											{state.name}
										</Option>
									))}
								</Select>
							</Form.Item>
						) : null}
					</Col>
					<Col xs={24} lg={10}>
						{/*<Form.Item label="ZIP" name="zip">*/}
						{/*	<Input disabled={!!auth.loading} />*/}
						{/*</Form.Item>*/}
						<Form.Item name="timezone" label="Timezone">
							<Select
								showSearch
								filterOption={(input, option) =>
									option['alias-name'].toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
									option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
								}
							>
								{momentTimezone.tz
									.names()
									.filter((name) => removeNonCountryTZ(name))
									.map((name: string, index) => {
										const aliasName = name.replace('_', ' ')
										return (
											<Option key={index} value={name} alias-name={aliasName}>
												(GMT {momentTimezone.tz(name).format('Z')}) {aliasName}
											</Option>
										)
									})}
							</Select>
						</Form.Item>
					</Col>
					<Col xs={24} lg={12}>
						<Form.Item
							label="Languages"
							name="languages"
							rules={[{ required: userData?.user?.isExpert, message: 'Please input preferred languages.' }]}
						>
							<Select mode="multiple" allowClear filterOption={onLanguagesSearch}>
								{languagesOptions.map((iso: string) => {
									return (
										<Option key={iso} value={iso}>
											{ISO6391.getName(iso)}
										</Option>
									)
								})}
							</Select>
						</Form.Item>
					</Col>
					<Col xs={24} lg={10}>
						<Space direction="vertical" style={{ width: '100%' }}>
							<Title level={4}>Social Links</Title>
							<Form.List name="socialLinks">
								{(fields: FormListFieldData[], { add, remove }) => (
									<>
										{fields.map(({ key, name, fieldKey, ...restField }) => (
											<Row key={key} align="bottom" gutter={[8, 0]}>
												<Col flex="auto">
													<Form.Item
														{...restField}
														name={[name, 'url']}
														fieldKey={[fieldKey, 'url']}
														rules={[
															{ required: true, message: 'Missing Url' },
															{
																message: 'Not valid Url!',
																validator: (_, value) => {
																	if (!socialLinks.isValid(networks.keyFor(value), value) && !value.match(regex)) {
																		return Promise.reject()
																	}
																	return Promise.resolve()
																},
															},
														]}
													>
														<Input
															prefix={
																<SocialIcon
																	style={{ height: 25, width: 25 }}
																	url={links[name] || ''}
																	onClick={(e: { preventDefault: () => any }) => e.preventDefault()}
																/>
															}
															placeholder="Url"
															onChange={onChangeUrl(name)}
														/>
													</Form.Item>
												</Col>
												<Col flex="none">
													<CloseCircleOutlined
														onClick={() => remove(name)}
														style={{ fontSize: '18px', marginBottom: 31 }}
													/>
												</Col>
											</Row>
										))}
										<Row>
											<Col span={24}>
												<Form.Item>
													<Button type="ghost" onClick={() => add()} icon={<PlusOutlined />}>
														Add Link
													</Button>
												</Form.Item>
											</Col>
										</Row>
									</>
								)}
							</Form.List>
						</Space>
					</Col>
				</Row>
				<Form.Item>
					<div className="btn-actions">
						<Button shape="round" type="primary" htmlType="submit" loading={!!userData?.loading}>
							Update
						</Button>
					</div>
				</Form.Item>
				{userData?.error && <Alert showIcon message="Error" description={userData?.error} type="error" />}
				{formErrors.length > 0 && (
					<Alert
						showIcon
						type="warning"
						message="Warning"
						description={formErrors.map((v) => (
							<Paragraph key={v.name + '_errors'} style={{ marginBottom: 0 }}>
								{v.errors[0]}
							</Paragraph>
						))}
					/>
				)}
			</Form>
			{resetPasswordModalVisibility && <ResetPasswordModal onClose={() => setResetPasswordModalVisibility(false)} />}
		</>
	)
}

export default AccountForm

function removeNonCountryTZ(name: string) {
	const withoutCountryCodes = [
		'CET',
		'CST6CDT',
		'EET',
		'EST',
		'EST5EDT',
		'Eire',
		'Greenwich',
		'HST',
		'MET',
		'MST',
		'MST7MDT',
		'PST8PDT',
		'UCT',
		'UTC',
		'Universal',
		'WET',
		'Zulu',
	]
	return !name.startsWith('Etc') && !name.startsWith('GMT') && withoutCountryCodes.indexOf(name) === -1
}
